import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsersGear,
  faPlus,
  faCheck,
  faXmark,
  faUserShield,
  faUserTie,
  faUser,
  faToggleOn,
  faToggleOff,
  faLock,
  faHandHoldingHeart,
  faBoxOpen,
  faSort,
  faSortUp,
  faSortDown,
  faDownload,
  faSearch,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faChevronLeft,
  faChevronRight,
  faCopy,
  faMapMarkerAlt,
  faIdCard,
  faShieldAlt,
  faInfoCircle,
  faGift,
  faExchangeAlt,
  faWarehouse,
  faLeaf,
  faShoppingCart,
  faChevronDown,
  faChevronUp,
  faCalendarAlt,
  faSpinner,
  faMagic,
  faClock,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import ActionButton from "../ui/ActionButton";
import PhoneInput from "../ui/PhoneInput";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useDebounce } from "../../hooks/useDebounce";
import { useDataCache, CACHE_KEYS } from "../../contexts/DataCacheContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  fetchUsers,
  fetchOrganizations,
  createUser,
  generateUserForOrg,
  updateUser,
  resetUserPassword,
  bulkUpdateUsers,
} from "../../api/adminApi";
import { validatePassword } from "../../utils/validation";

const DEFAULT_TAB_PERMISSIONS = {
  donor: {
    donate_materials: true,
    allocate: true,
    inventory: true,
    ghg: true,
  },
  receiver: {
    browse_materials: true,
    schedule: true,
    inventory: true,
    ghg: true,
  },
  shared: {
    partnerships: true,
  },
};

const hasFormChanges = (original, current) => {
  if (!original || !current) return false;
  return JSON.stringify(original) !== JSON.stringify(current);
};

const getRelativeTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
};

function UserManagementSection({ darkMode, t }) {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [originalEditingUser, setOriginalEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOrg, setFilterOrg] = useState("all");
  const [showLegacyAccounts, setShowLegacyAccounts] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [resetPassword, setResetPassword] = useState("");

  // caching state
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const { getCachedData, setCachedData } = useDataCache();
  const isFetching = useRef(false);

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedAddressId, setCopiedAddressId] = useState(null);
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsToShow, setCredentialsToShow] = useState({
    username: "",
    password: "",
    expiresAt: null,
  });
  const [copiedField, setCopiedField] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [bulkTogglingIds, setBulkTogglingIds] = useState([]);
  const [bulkTogglingAction, setBulkTogglingAction] = useState(null); // "activate" or "deactivate"
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showPermissionsPanel, setShowPermissionsPanel] = useState(false);
  const [createMode, setCreateMode] = useState("manual"); // "generate" or "manual"
  const itemsPerPage = 10;
  const [toastFading, setToastFading] = useState(false);

  // auto-dismiss snackbar after 3 seconds with fade out
  useEffect(() => {
    if (snackbar.open) {
      const fadeTimer = setTimeout(() => {
        setToastFading(true);
      }, 2500);

      const closeTimer = setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
        setToastFading(false);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(closeTimer);
      };
    }
  }, [snackbar.open]);

  const detailPanelRef = useRef(null);
  const sectionTopRef = useRef(null);

  const scrollToTop = () => {
    setTimeout(() => {
      sectionTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  // debounce search for performance
  const debouncedSearch = useDebounce(searchQuery, 300);

  // load users and organizations with caching
  const loadData = useCallback(
    async (forceRefresh = false) => {
      if (isFetching.current) return;

      // check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedUsers = getCachedData(CACHE_KEYS.ADMIN_USERS);
        const cachedOrgs = getCachedData(CACHE_KEYS.ADMIN_ORGANIZATIONS);

        if (cachedUsers?.data && cachedOrgs?.data) {
          console.log(
            "Using cached admin data (age: " +
              Math.round((Date.now() - cachedUsers.timestamp) / 1000 / 60) +
              " min)",
          );
          setUsers(cachedUsers.data.users || []);
          setOrganizations(cachedOrgs.data.organizations || []);
          setLastUpdated(cachedUsers.timestamp);
          setLoading(false);
          return;
        }
      }

      isFetching.current = true;

      try {
        if (forceRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        console.log("Fetching fresh admin data from API...");

        const [usersData, orgsData] = await Promise.all([
          fetchUsers(),
          fetchOrganizations(),
        ]);

        const timestamp = Date.now();

        setUsers(usersData.users || []);
        setOrganizations(orgsData.organizations || []);
        setLastUpdated(timestamp);

        setCachedData(CACHE_KEYS.ADMIN_USERS, usersData, timestamp);
        setCachedData(CACHE_KEYS.ADMIN_ORGANIZATIONS, orgsData, timestamp);

        console.log("Fresh admin data fetched and cached");
      } catch (err) {
        console.error("Failed to load data:", err);
        setError(t("Failed to load users"));
      } finally {
        setLoading(false);
        setRefreshing(false);
        isFetching.current = false;
      }
    },
    [t, getCachedData, setCachedData],
  );

  const handleRefresh = useCallback(() => {
    loadData(true);
  }, [loadData]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  const handleCopyAddress = (e, userId, address) => {
    e.stopPropagation();
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopiedAddressId(userId);
    setTimeout(() => setCopiedAddressId(null), 2000);
  };

  const handleCopyEmail = (e, uniqueId, email) => {
    e.stopPropagation();
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmailId(uniqueId);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  // open address in Google Maps
  const handleOpenInMaps = (e, address) => {
    e.stopPropagation();
    if (!address) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // scroll to detail panel when it opens
  const scrollToDetailPanel = () => {
    setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const [newUser, setNewUser] = useState({
    organization_id: "",
    is_company_admin: false,
    can_donate: true,
    can_receive: true,
    tab_permissions: { ...DEFAULT_TAB_PERMISSIONS },
    // manual mode fields
    username: "",
    email: "",
    phone_number: "",
    company_name: "",
    address: "",
    city: "",
    temp_password: "",
  });

  // get user type for sorting
  const getUserTypeRank = (user) => {
    if (user.is_master_admin) return 0;
    if (user.is_company_admin) return 1;
    return 2;
  };

  // filter and sort
  const filteredUsers = useMemo(() => {
    let result = users.filter((user) => {
      const searchLower = debouncedSearch.toLowerCase();
      const matchesSearch =
        (user.username || "").toLowerCase().includes(searchLower) ||
        (user.email || "").toLowerCase().includes(searchLower) ||
        (user.company_name &&
          user.company_name.toLowerCase().includes(searchLower)) ||
        (user.city && user.city.toLowerCase().includes(searchLower));

      const matchesOrg =
        filterOrg === "all"
          ? showLegacyAccounts
            ? true
            : user.organization_id !== null
          : filterOrg === "legacy"
            ? !user.organization_id
            : user.organization_id === filterOrg;

      const matchesType =
        filterType === "all" ||
        (filterType === "master" && user.is_master_admin) ||
        (filterType === "company-admin" && user.is_company_admin) ||
        (filterType === "sub-account" &&
          !user.is_master_admin &&
          !user.is_company_admin);

      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && user.is_active) ||
        (filterStatus === "inactive" && !user.is_active);

      return matchesSearch && matchesOrg && matchesType && matchesStatus;
    });

    result.sort((a, b) => {
      let aVal, bVal;

      if (sortConfig.key === "type") {
        aVal = getUserTypeRank(a);
        bVal = getUserTypeRank(b);
      } else if (sortConfig.key === "location") {
        aVal = a.address || "";
        bVal = b.address || "";
      } else {
        aVal = a[sortConfig.key] ?? "";
        bVal = b[sortConfig.key] ?? "";
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [
    users,
    debouncedSearch,
    filterOrg,
    filterType,
    filterStatus,
    sortConfig,
    showLegacyAccounts,
  ]);

  // reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    filterOrg,
    filterType,
    filterStatus,
    showLegacyAccounts,
  ]);

  // client-side pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const toggleSelectAll = () => {
    const selectableUsers = filteredUsers.filter((u) => u.id !== currentUserId);
    const allSelectableSelected = selectableUsers.every((u) =>
      selectedIds.includes(u.id),
    );

    if (allSelectableSelected && selectableUsers.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableUsers.map((u) => u.id));
    }
  };

  const toggleSelect = (id) => {
    if (id === currentUserId) return; // prevent selecting current user
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const getUserTypeIcon = (user) => {
    if (user.is_master_admin) return faUserShield;
    if (user.is_company_admin) return faUserTie;
    return faUser;
  };

  const getUserTypeLabel = (user) => {
    if (user.is_master_admin) return t("Master Admin");
    if (user.is_company_admin) return t("Company Admin");
    return t("Sub-account");
  };

  const getUserTypeClass = (user) => {
    if (user.is_master_admin) return "master";
    if (user.is_company_admin) return "company-admin";
    return "sub-account";
  };

  // check if a user's organization is disabled
  const isUserOrgDisabled = (user) => {
    if (!user.organization_id) return false;
    const org = organizations.find((o) => o.id === user.organization_id);
    return org && !org.is_active;
  };

  const handleGenerateUser = async () => {
    setIsGenerating(true);
    try {
      // build permissions object (only for non-admin users)
      const permissions = newUser.is_company_admin
        ? null
        : {
            can_donate: newUser.can_donate,
            can_receive: newUser.can_receive,
            tab_permissions: newUser.tab_permissions,
          };

      const result = await generateUserForOrg(
        newUser.organization_id,
        newUser.is_company_admin,
        permissions,
      );

      // show credentials modal with expiry info
      setCredentialsToShow({
        username: result.username,
        password: result.password,
        expiresAt: result.expires_at,
      });
      setShowCredentialsModal(true);

      // reset form
      resetNewUserForm();
      setShowCreateModal(false);

      // force refresh to update cache
      await loadData(true);
    } catch (err) {
      console.error("Failed to generate user:", err);
      setError(t("Failed to create user: ") + (err.message || "Unknown error"));
    } finally {
      setIsGenerating(false);
    }
  };

  const generateTempPassword = () => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const all = lower + upper + numbers;

    let pass = [
      lower.charAt(Math.floor(Math.random() * lower.length)),
      upper.charAt(Math.floor(Math.random() * upper.length)),
      numbers.charAt(Math.floor(Math.random() * numbers.length)),
    ];

    for (let i = 0; i < 9; i++) {
      pass.push(all.charAt(Math.floor(Math.random() * all.length)));
    }

    for (let i = pass.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pass[i], pass[j]] = [pass[j], pass[i]];
    }

    setNewUser({ ...newUser, temp_password: pass.join("") });
  };

  const handleManualCreate = async () => {
    setIsCreating(true);
    try {
      const userData = {
        username: newUser.username,
        email: newUser.email || null,
        password: newUser.temp_password,
        phone_number: newUser.phone_number || null,
        organization_id: newUser.organization_id || null,
        company_name: newUser.is_company_admin
          ? null
          : newUser.company_name || null,
        address: newUser.is_company_admin ? null : newUser.address || null,
        city: newUser.is_company_admin ? null : newUser.city || null,
        is_company_admin: newUser.is_company_admin,
        can_donate: newUser.is_company_admin ? false : newUser.can_donate,
        can_receive: newUser.is_company_admin ? false : newUser.can_receive,
        tab_permissions: newUser.is_company_admin
          ? null
          : newUser.tab_permissions,
      };

      await createUser(userData);

      // show credentials modal
      setCredentialsToShow({
        username: newUser.username,
        password: newUser.temp_password,
        expiresAt: null,
      });
      setShowCredentialsModal(true);

      // reset form
      resetNewUserForm();
      setShowCreateModal(false);

      // force refresh
      await loadData(true);
    } catch (err) {
      console.error("Failed to create user:", err);
      setError(t("Failed to create user: ") + (err.message || "Unknown error"));
    } finally {
      setIsCreating(false);
    }
  };

  const resetNewUserForm = () => {
    setNewUser({
      organization_id: "",
      is_company_admin: false,
      can_donate: true,
      can_receive: true,
      tab_permissions: { ...DEFAULT_TAB_PERMISSIONS },
      username: "",
      email: "",
      phone_number: "",
      company_name: "",
      address: "",
      city: "",
      temp_password: "",
    });
    setShowPermissionsPanel(false);
    setCreateMode("generate");
    setShowTempPassword(false);
  };

  // check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return hasFormChanges(originalEditingUser, editingUser) || resetPassword;
  };

  // try to close edit panel - show modal if unsaved changes
  const tryCloseEdit = (action = null) => {
    if (hasUnsavedChanges()) {
      setPendingAction(() => action);
      setShowUnsavedModal(true);
    } else {
      closeEditPanel();
      if (action) action();
    }
  };

  // actually close the edit panel
  const closeEditPanel = () => {
    setEditingUser(null);
    setOriginalEditingUser(null);
    setShowPassword(false);
    setResetPassword("");
  };

  const confirmDiscard = () => {
    setShowUnsavedModal(false);
    const action = pendingAction;
    closeEditPanel();
    setPendingAction(null);
    if (action) action();
  };

  const cancelDiscard = () => {
    setShowUnsavedModal(false);
    setPendingAction(null);
  };

  const handleEdit = (user) => {
    setOriginalEditingUser({ ...user });
    setEditingUser({ ...user });
    setShowCreateModal(false);
    setResetPassword("");
    setShowPassword(false);

    // select the row
    if (!selectedIds.includes(user.id)) {
      setSelectedIds([user.id]);
    }
    scrollToDetailPanel();
  };

  const generateResetPassword = () => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const all = lower + upper + numbers;

    // ensure at least one of each required type
    let pass = [
      lower.charAt(Math.floor(Math.random() * lower.length)),
      upper.charAt(Math.floor(Math.random() * upper.length)),
      numbers.charAt(Math.floor(Math.random() * numbers.length)),
    ];

    // fill remaining chars
    for (let i = 0; i < 9; i++) {
      pass.push(all.charAt(Math.floor(Math.random() * all.length)));
    }

    // shuffle
    for (let i = pass.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pass[i], pass[j]] = [pass[j], pass[i]];
    }

    setResetPassword(pass.join(""));
    setShowPassword(true);
  };

  const handleSetPassword = async () => {
    setIsSaving(true);
    try {
      await resetUserPassword(editingUser.id, resetPassword);

      // show credentials modal
      setCredentialsToShow({
        username: editingUser.username,
        password: resetPassword,
      });
      setShowCredentialsModal(true);

      // clear password from UI
      setResetPassword("");
      setShowPassword(false);
    } catch (err) {
      console.error("Failed to reset password:", err);
      setError(t("Failed to reset password"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await updateUser(editingUser.id, {
        username: editingUser.username,
        email: editingUser.email,
        phone_number: editingUser.phone_number,
        company_name: editingUser.company_name,
        address: editingUser.address,
        city: editingUser.city,
        organization_id: editingUser.organization_id,
        is_active: editingUser.is_active,
        is_company_admin: editingUser.is_company_admin,
        can_donate: editingUser.can_donate,
        can_receive: editingUser.can_receive,
        tab_permissions: editingUser.tab_permissions,
      });

      setEditingUser(null);
      setOriginalEditingUser(null);

      // force refresh to update cache
      await loadData(true);

      setSnackbar({
        open: true,
        message: "User updated successfully",
        severity: "success",
      });

      // scroll to top
      scrollToTop();
    } catch (err) {
      console.error("Failed to update user:", err);
      setError(t("Failed to update user"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleUserActive = async (userId) => {
    if (togglingId || bulkTogglingIds.length > 0) return;

    const user = users.find((u) => u.id === userId);
    if (!user) return;

    setTogglingId(userId);
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 600;
    const newStatus = !user.is_active;

    try {
      await updateUser(userId, { is_active: newStatus });

      // ensure minimum display time
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      // force refresh to update cache and lastUpdated timestamp
      await loadData(true);

      // show warning if activating user in disabled org
      if (newStatus && isUserOrgDisabled(user)) {
        setSnackbar({
          open: true,
          message:
            "User activated, but won't be able to log in until their organization is activated",
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: newStatus ? "User activated" : "User deactivated",
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Failed to toggle user status:", err);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      const errorMsg = err.message || String(err);
      if (
        errorMsg.includes("rate limit") ||
        errorMsg.includes("limit reached") ||
        errorMsg.includes("429")
      ) {
        setError(t("Status change limit reached. Try again later."));
      } else {
        setError(t("Failed to update user: ") + errorMsg);
      }
    } finally {
      setTogglingId(null);
    }
  };

  const bulkSetActive = async (active) => {
    if (bulkTogglingIds.length > 0 || togglingId) return;

    const eligibleIds = selectedIds.filter((id) => {
      const user = users.find((u) => u.id === id);
      return user && !user.is_master_admin && id !== currentUserId;
    });

    if (eligibleIds.length === 0) return;

    setBulkTogglingIds([...eligibleIds]);
    setBulkTogglingAction(active ? "activate" : "deactivate");
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 600;
    const count = eligibleIds.length;

    try {
      await bulkUpdateUsers(eligibleIds, active);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      setSelectedIds([]);
      await loadData(true);

      // check if any activated users are in disabled orgs
      if (active) {
        const usersInDisabledOrgs = eligibleIds.filter((id) => {
          const user = users.find((u) => u.id === id);
          return user && isUserOrgDisabled(user);
        });

        if (usersInDisabledOrgs.length > 0) {
          setSnackbar({
            open: true,
            message: `${count} user${count > 1 ? "s" : ""} activated, but ${usersInDisabledOrgs.length} won't be able to log in until their organization is activated`,
            severity: "warning",
          });
        } else {
          setSnackbar({
            open: true,
            message: `${count} user${count > 1 ? "s" : ""} activated`,
            severity: "success",
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: `${count} user${count > 1 ? "s" : ""} deactivated`,
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Failed to bulk update users:", err);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      const errorMsg = err.message || String(err);
      if (
        errorMsg.includes("rate limit") ||
        errorMsg.includes("limit reached") ||
        errorMsg.includes("429")
      ) {
        setError(
          t("Status change limit reached for some users. Try again later."),
        );
      } else {
        setError(t("Failed to update users: ") + errorMsg);
      }
    } finally {
      setBulkTogglingIds([]);
      setBulkTogglingAction(null);
    }
  };

  const exportCSV = () => {
    const headers = [
      "Username",
      "Email",
      "Phone",
      "Location",
      "City",
      "Organization",
      "Type",
      "Donor",
      "Receiver",
      "Status",
    ];
    const rows = filteredUsers.map((user) => [
      user.username,
      user.email,
      user.phone_number || "-",
      user.company_name || "-",
      user.city || "-",
      user.organization_name || "-",
      getUserTypeLabel(user),
      user.can_donate ? "Yes" : "No",
      user.can_receive ? "Yes" : "No",
      user.is_active ? "Active" : "Inactive",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    a.click();
  };

  // check if current user can demote (only site admin can demote)
  const canDemote = true;

  const selectableUsers = filteredUsers.filter((u) => u.id !== currentUserId);
  const allSelected =
    selectableUsers.length > 0 && selectedIds.length === selectableUsers.length;
  const someSelected = selectedIds.length > 0;

  if (loading && users.length === 0) {
    return (
      <div className="profile-section admin-section-wide">
        <div className="admin-loading">
          <LoadingSpinner size={40} />
          <span>{t("Loading users...")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section admin-section-wide" ref={sectionTopRef}>
      <div className="profile-section-header">
        <div className="profile-section-icon admin-icon">
          <FontAwesomeIcon icon={faUsersGear} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="profile-section-title">{t("User Management")}</h2>
          <p className="profile-section-desc">
            {t("Manage all user accounts across organizations")}
          </p>
        </div>
        <div className="header-status-area">
          {snackbar.open && (
            <div
              className={`inline-toast ${snackbar.severity} ${toastFading ? "fading" : ""}`}
            >
              <FontAwesomeIcon
                icon={
                  snackbar.severity === "warning"
                    ? faExclamationTriangle
                    : faCheck
                }
              />
              <span>{t(snackbar.message)}</span>
            </div>
          )}
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="admin"
          />
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {/* toolbar */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-left">
          <div className="admin-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder={t("Search users...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="admin-filter-select"
            value={filterOrg}
            onChange={(e) => setFilterOrg(e.target.value)}
          >
            <option value="all">{t("All Organizations")}</option>
            <option value="legacy">{t("Legacy (No Org)")}</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          <label className="admin-legacy-toggle">
            <input
              type="checkbox"
              checked={showLegacyAccounts}
              onChange={(e) => setShowLegacyAccounts(e.target.checked)}
            />
            <span>{t("Show legacy")}</span>
          </label>
          <select
            className="admin-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">{t("All Types")}</option>
            <option value="master">{t("Master Admin")}</option>
            <option value="company-admin">{t("Company Admin")}</option>
            <option value="sub-account">{t("Sub-account")}</option>
          </select>
          <select
            className="admin-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{t("All Status")}</option>
            <option value="active">{t("Active")}</option>
            <option value="inactive">{t("Inactive")}</option>
          </select>
        </div>
        <div className="admin-toolbar-right">
          <button
            className="admin-toolbar-btn"
            onClick={exportCSV}
            title={t("Export CSV")}
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            className="admin-toolbar-btn admin-primary"
            onClick={() => {
              setShowCreateModal(true);
              setEditingUser(null);
              setOriginalEditingUser(null);
              scrollToDetailPanel();
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("New User")}</span>
          </button>
        </div>
      </div>

      {/* bulk action bar */}
      {someSelected && (
        <div className="admin-bulk-bar">
          <span className="admin-bulk-count">
            {selectedIds.length} {t("selected")}
          </span>
          <div className="admin-bulk-actions">
            <button
              className="admin-bulk-btn success"
              onClick={() => bulkSetActive(true)}
              disabled={bulkTogglingIds.length > 0 || !!togglingId}
            >
              <FontAwesomeIcon
                icon={
                  bulkTogglingAction === "activate" ? faSpinner : faToggleOn
                }
                spin={bulkTogglingAction === "activate"}
              />
              {bulkTogglingAction === "activate"
                ? t("Activating...")
                : t("Activate")}
            </button>
            <button
              className="admin-bulk-btn warning"
              onClick={() => bulkSetActive(false)}
              disabled={bulkTogglingIds.length > 0 || !!togglingId}
            >
              <FontAwesomeIcon
                icon={
                  bulkTogglingAction === "deactivate" ? faSpinner : faToggleOff
                }
                spin={bulkTogglingAction === "deactivate"}
              />
              {bulkTogglingAction === "deactivate"
                ? t("Deactivating...")
                : t("Deactivate")}
            </button>
            <button
              className="admin-bulk-btn"
              onClick={() => setSelectedIds([])}
              disabled={bulkTogglingIds.length > 0}
            >
              <FontAwesomeIcon icon={faXmark} />
              {t("Clear")}
            </button>
          </div>
        </div>
      )}

      {/* table */}
      <div
        className={`admin-table-container ${refreshing ? "shimmer-overlay" : ""}`}
      >
        <table className="admin-table">
          <thead>
            <tr>
              <th className="th-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="sortable" onClick={() => handleSort("username")}>
                {t("Username")}
                <FontAwesomeIcon
                  icon={getSortIcon("username")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("type")}>
                {t("Role")}
                <FontAwesomeIcon
                  icon={getSortIcon("type")}
                  className="sort-icon"
                />
              </th>
              <th
                className="sortable"
                onClick={() => handleSort("organization_name")}
              >
                {t("Organization")}
                <FontAwesomeIcon
                  icon={getSortIcon("organization_name")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("location")}>
                {t("Location")}
                <FontAwesomeIcon
                  icon={getSortIcon("location")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("created_at")}>
                {t("Created")}
                <FontAwesomeIcon
                  icon={getSortIcon("created_at")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("last_login")}>
                {t("Last Login")}
                <FontAwesomeIcon
                  icon={getSortIcon("last_login")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("is_active")}>
                {t("Status")}
                <FontAwesomeIcon
                  icon={getSortIcon("is_active")}
                  className="sort-icon"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => {
              const isSelected = selectedIds.includes(user.id);
              const isEditing = editingUser?.id === user.id;
              const isCurrentUser = user.id === currentUserId;

              return (
                <tr
                  key={user.id}
                  className={`${!user.is_active ? "row-inactive" : ""} ${isSelected ? "row-selected" : ""} ${isEditing ? "row-editing" : ""} ${isCurrentUser ? "row-current-user" : ""}`}
                  onClick={() => !isCurrentUser && handleEdit(user)}
                  style={{ cursor: isCurrentUser ? "default" : "pointer" }}
                >
                  <td
                    className="td-checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(user.id)}
                      disabled={isCurrentUser}
                    />
                  </td>
                  <td>
                    <div className="cell-user">
                      <div className={`user-avatar ${getUserTypeClass(user)}`}>
                        <FontAwesomeIcon icon={getUserTypeIcon(user)} />
                      </div>
                      <div className="user-info">
                        <span className="cell-primary">
                          {user.username}
                          {isCurrentUser && (
                            <span className="you-badge">{t("(You)")}</span>
                          )}
                        </span>
                        <div className="cell-email-with-copy">
                          <span className="cell-secondary">{user.email}</span>
                          <div className="email-actions">
                            <button
                              className="address-icon-btn"
                              onClick={(e) =>
                                handleCopyEmail(
                                  e,
                                  `email-${user.id}`,
                                  user.email,
                                )
                              }
                            >
                              <span
                                className={`copied-tooltip ${copiedEmailId === `email-${user.id}` ? "show" : ""}`}
                              >
                                {t("Copied!")}
                              </span>
                              <FontAwesomeIcon
                                icon={
                                  copiedEmailId === `email-${user.id}`
                                    ? faCheck
                                    : faCopy
                                }
                                className={`address-icon ${copiedEmailId === `email-${user.id}` ? "copied" : ""}`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="cell-role">
                      <span className={`type-badge ${getUserTypeClass(user)}`}>
                        <FontAwesomeIcon icon={getUserTypeIcon(user)} />
                        {getUserTypeLabel(user)}
                      </span>
                      {!!user.can_donate && (
                        <Tooltip title={t("Donor")} arrow placement="top">
                          <span className="perm-icon-pill donor">
                            <FontAwesomeIcon icon={faHandHoldingHeart} />
                          </span>
                        </Tooltip>
                      )}
                      {!!user.can_receive && (
                        <Tooltip title={t("Receiver")} arrow placement="top">
                          <span className="perm-icon-pill receiver">
                            <FontAwesomeIcon icon={faBoxOpen} />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td>
                    {user.organization_name ? (
                      <span className="cell-org-name">
                        {user.organization_name}
                      </span>
                    ) : (
                      <span className="cell-muted">—</span>
                    )}
                  </td>
                  <td>
                    {user.address ? (
                      (() => {
                        const fullAddress = user.city
                          ? `${user.address}, ${user.city}`
                          : user.address;
                        const isCopied = copiedAddressId === user.id;
                        return (
                          <div className="cell-location-with-actions">
                            <div className="cell-location">
                              <span className="cell-primary">
                                {user.address}
                              </span>
                              {user.city && (
                                <span className="cell-secondary">
                                  {user.city}
                                </span>
                              )}
                            </div>
                            <div className="address-actions">
                              <button
                                className="address-icon-btn"
                                onClick={(e) =>
                                  handleCopyAddress(e, user.id, fullAddress)
                                }
                              >
                                <span
                                  className={`copied-tooltip ${isCopied ? "show" : ""}`}
                                >
                                  {t("Copied!")}
                                </span>
                                <FontAwesomeIcon
                                  icon={isCopied ? faCheck : faCopy}
                                  className={`address-icon ${isCopied ? "copied" : ""}`}
                                />
                              </button>
                              <button
                                className="address-icon-btn"
                                onClick={(e) =>
                                  handleOpenInMaps(e, fullAddress)
                                }
                                title={t("Open in Google Maps")}
                              >
                                <FontAwesomeIcon
                                  icon={faMapMarkerAlt}
                                  className="address-icon map-icon"
                                />
                              </button>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <span className="cell-muted">—</span>
                    )}
                  </td>
                  <td>
                    {user.created_at ? (
                      <div className="cell-date-combo">
                        <span className="date-text">
                          {new Date(user.created_at).toLocaleDateString(
                            "en-US",
                            { month: "short", day: "numeric", year: "numeric" },
                          )}
                        </span>
                        <span className="date-relative-pill">
                          {getRelativeTime(user.created_at)}
                        </span>
                      </div>
                    ) : (
                      <span className="cell-muted">—</span>
                    )}
                  </td>
                  <td>
                    {user.last_login ? (
                      <div className="cell-date-combo">
                        <span className="date-text">
                          {new Date(user.last_login).toLocaleDateString(
                            "en-US",
                            { month: "short", day: "numeric", year: "numeric" },
                          )}
                        </span>
                        <span className="date-relative-pill">
                          {getRelativeTime(user.last_login)}
                        </span>
                      </div>
                    ) : (
                      <span className="cell-muted">Never</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="status-cell">
                      {togglingId === user.id ||
                      bulkTogglingIds.includes(user.id) ? (
                        <span
                          className={`status-pill ${bulkTogglingAction === "activate" || (!bulkTogglingAction && !user.is_active) ? "activating" : "deactivating"}`}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin />
                          {bulkTogglingAction === "activate" ||
                          (!bulkTogglingAction && !user.is_active)
                            ? t("Activating...")
                            : t("Deactivating...")}
                        </span>
                      ) : (
                        <button
                          className={`status-pill ${user.is_active ? "active" : "inactive"}`}
                          onClick={() => toggleUserActive(user.id)}
                          disabled={
                            user.is_master_admin ||
                            isCurrentUser ||
                            !!togglingId ||
                            bulkTogglingIds.length > 0
                          }
                        >
                          <FontAwesomeIcon
                            icon={user.is_active ? faToggleOn : faToggleOff}
                          />
                          {user.is_active ? t("Active") : t("Inactive")}
                        </button>
                      )}
                      {isUserOrgDisabled(user) && (
                        <Tooltip
                          title={t(
                            "User cannot log in because their organization is disabled",
                          )}
                          arrow
                          placement="top"
                        >
                          <span className="org-disabled-badge">
                            {t("Org disabled")}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faUsersGear} />
            <p>{t("No users match your filters")}</p>
          </div>
        )}
      </div>

      {/* table footer with pagination */}
      <div className="admin-table-footer">
        <span className="table-count">
          {t("Showing")}{" "}
          {Math.min((currentPage - 1) * itemsPerPage + 1, filteredUsers.length)}
          -{Math.min(currentPage * itemsPerPage, filteredUsers.length)}{" "}
          {t("of")} {filteredUsers.length}
        </span>
        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => {
                setCurrentPage((p) => Math.max(1, p - 1));
                scrollToTop();
              }}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="pagination-info">
              {currentPage} / {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => {
                setCurrentPage((p) => Math.min(totalPages, p + 1));
                scrollToTop();
              }}
              disabled={currentPage === totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* inline create panel - manual by default with auto-generate option */}
      {showCreateModal && (
        <div
          className="admin-detail-panel"
          ref={detailPanelRef}
          style={{ scrollMarginTop: "100px" }}
        >
          <div className="admin-detail-header">
            <div className="admin-detail-title">
              <div className="user-avatar sub-account">
                <FontAwesomeIcon icon={faPlus} />
              </div>
              <div>
                <h3>{t("Create User")}</h3>
                <span>{t("Add a new user to an organization")}</span>
              </div>
            </div>
            <div className="header-right-controls">
              <div
                className={`easy-creation-toggle ${createMode === "generate" ? "active" : ""}`}
                onClick={() =>
                  setCreateMode(
                    createMode === "generate" ? "manual" : "generate",
                  )
                }
              >
                <FontAwesomeIcon
                  icon={createMode === "generate" ? faToggleOn : faToggleOff}
                />
                <span>{t("Easy Creation")}</span>
              </div>
              <button
                className="admin-detail-close"
                onClick={() => {
                  resetNewUserForm();
                  setShowCreateModal(false);
                }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          <div className="admin-detail-body">
            {/* Organization & Account Type - always shown */}
            <div className="admin-detail-grid">
              <div className="admin-detail-field">
                <label>{t("Organization")} *</label>
                <select
                  className="profile-select"
                  value={newUser.organization_id}
                  onChange={(e) =>
                    setNewUser({ ...newUser, organization_id: e.target.value })
                  }
                >
                  <option value="">{t("Select organization...")}</option>
                  {organizations
                    .filter((org) => org.is_active)
                    .map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="admin-detail-field">
                <label>{t("Account Type")}</label>
                <div className="admin-type-toggle">
                  <button
                    className={`admin-type-btn ${!newUser.is_company_admin ? "active" : ""}`}
                    onClick={() =>
                      setNewUser({
                        ...newUser,
                        is_company_admin: false,
                        can_donate: true,
                        can_receive: true,
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faUser} />
                    {t("Company User")}
                  </button>
                  <button
                    className={`admin-type-btn ${newUser.is_company_admin ? "active" : ""}`}
                    onClick={() =>
                      setNewUser({ ...newUser, is_company_admin: true })
                    }
                  >
                    <FontAwesomeIcon icon={faUserTie} />
                    {t("Company Admin")}
                  </button>
                </div>
              </div>
            </div>

            {/* GENERATE MODE - show generate hero */}
            {createMode === "generate" && (
              <>
                <div className="generate-account-hero">
                  <div className="generate-info">
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="generate-info-icon"
                    />
                    <div className="generate-info-text">
                      <p>{t("Click below to generate login credentials.")}</p>
                      <p className="generate-info-sub">
                        {t(
                          "The user will set their email and profile on first login. Credentials expire in 48 hours.",
                        )}
                      </p>
                    </div>
                  </div>
                  <ActionButton
                    onClick={handleGenerateUser}
                    variant="secondary"
                    size="lg"
                    loading={isGenerating}
                    disabled={!newUser.organization_id}
                  >
                    <FontAwesomeIcon icon={faMagic} />
                    {t("Generate Credentials")}
                  </ActionButton>
                </div>

                {/* Customize Permissions - only for non-admin users */}
                {!newUser.is_company_admin && (
                  <div className="admin-detail-section">
                    <button
                      className="permissions-toggle-btn"
                      onClick={() =>
                        setShowPermissionsPanel(!showPermissionsPanel)
                      }
                    >
                      <FontAwesomeIcon
                        icon={
                          showPermissionsPanel ? faChevronUp : faChevronDown
                        }
                      />
                      {t("Customize Permissions")}
                      <span className="permissions-toggle-hint">
                        {t("(optional)")}
                      </span>
                    </button>

                    {showPermissionsPanel && (
                      <div className="permissions-customize-panel">
                        {/* Donor Permissions */}
                        <div
                          className={`admin-permission-group ${newUser.can_donate ? "expanded" : ""}`}
                        >
                          <div
                            className="admin-permission-header"
                            onClick={() =>
                              setNewUser({
                                ...newUser,
                                can_donate: !newUser.can_donate,
                                tab_permissions: !newUser.can_donate
                                  ? {
                                      ...newUser.tab_permissions,
                                      donor: DEFAULT_TAB_PERMISSIONS.donor,
                                    }
                                  : newUser.tab_permissions,
                              })
                            }
                          >
                            <div className="admin-permission-toggle">
                              <FontAwesomeIcon
                                icon={
                                  newUser.can_donate ? faToggleOn : faToggleOff
                                }
                                className={newUser.can_donate ? "active" : ""}
                              />
                            </div>
                            <FontAwesomeIcon
                              icon={faHandHoldingHeart}
                              className="admin-permission-icon donor"
                            />
                            <span className="admin-permission-title">
                              {t("Donor Access")}
                            </span>
                            <FontAwesomeIcon
                              icon={
                                newUser.can_donate ? faChevronUp : faChevronDown
                              }
                              className="admin-permission-chevron"
                            />
                          </div>
                          {!!newUser.can_donate && (
                            <div className="admin-permission-tabs">
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.donor
                                      ?.donate_materials ?? true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        donor: {
                                          ...newUser.tab_permissions?.donor,
                                          donate_materials: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faGift} />
                                <span>{t("Donate Materials")}</span>
                              </label>
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.donor?.allocate ??
                                    true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        donor: {
                                          ...newUser.tab_permissions?.donor,
                                          allocate: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{t("Allocate")}</span>
                              </label>
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.donor?.inventory ??
                                    true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        donor: {
                                          ...newUser.tab_permissions?.donor,
                                          inventory: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faWarehouse} />
                                <span>{t("Inventory")}</span>
                              </label>
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.donor?.ghg ?? true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        donor: {
                                          ...newUser.tab_permissions?.donor,
                                          ghg: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faLeaf} />
                                <span>{t("GHG Dashboard")}</span>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Receiver Permissions */}
                        <div
                          className={`admin-permission-group ${newUser.can_receive ? "expanded" : ""}`}
                        >
                          <div
                            className="admin-permission-header"
                            onClick={() =>
                              setNewUser({
                                ...newUser,
                                can_receive: !newUser.can_receive,
                                tab_permissions: !newUser.can_receive
                                  ? {
                                      ...newUser.tab_permissions,
                                      receiver:
                                        DEFAULT_TAB_PERMISSIONS.receiver,
                                    }
                                  : newUser.tab_permissions,
                              })
                            }
                          >
                            <div className="admin-permission-toggle">
                              <FontAwesomeIcon
                                icon={
                                  newUser.can_receive ? faToggleOn : faToggleOff
                                }
                                className={newUser.can_receive ? "active" : ""}
                              />
                            </div>
                            <FontAwesomeIcon
                              icon={faBoxOpen}
                              className="admin-permission-icon receiver"
                            />
                            <span className="admin-permission-title">
                              {t("Receiver Access")}
                            </span>
                            <FontAwesomeIcon
                              icon={
                                newUser.can_receive
                                  ? faChevronUp
                                  : faChevronDown
                              }
                              className="admin-permission-chevron"
                            />
                          </div>
                          {!!newUser.can_receive && (
                            <div className="admin-permission-tabs">
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.receiver
                                      ?.browse_materials ?? true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        receiver: {
                                          ...newUser.tab_permissions?.receiver,
                                          browse_materials: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faShoppingCart} />
                                <span>{t("Browse Materials")}</span>
                              </label>
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.receiver
                                      ?.schedule ?? true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        receiver: {
                                          ...newUser.tab_permissions?.receiver,
                                          schedule: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                <span>{t("Schedule")}</span>
                              </label>
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.receiver
                                      ?.inventory ?? true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        receiver: {
                                          ...newUser.tab_permissions?.receiver,
                                          inventory: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faWarehouse} />
                                <span>{t("Inventory")}</span>
                              </label>
                              <label className="admin-tab-checkbox">
                                <input
                                  type="checkbox"
                                  checked={
                                    newUser.tab_permissions?.receiver?.ghg ??
                                    true
                                  }
                                  onChange={(e) =>
                                    setNewUser({
                                      ...newUser,
                                      tab_permissions: {
                                        ...newUser.tab_permissions,
                                        receiver: {
                                          ...newUser.tab_permissions?.receiver,
                                          ghg: e.target.checked,
                                        },
                                      },
                                    })
                                  }
                                />
                                <FontAwesomeIcon icon={faLeaf} />
                                <span>{t("GHG Dashboard")}</span>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Shared Permissions */}
                        <div className="admin-permission-group expanded" style={{ marginTop: "8px" }}>
                          <div className="admin-permission-header" style={{ cursor: "default" }}>
                            <FontAwesomeIcon icon={faHandshake} className="admin-permission-icon" style={{ color: "#0d9488" }} />
                            <span className="admin-permission-title">{t("Shared Features")}</span>
                          </div>
                          <div className="admin-permission-tabs">
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={newUser.tab_permissions?.shared?.partnerships ?? true}
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      shared: { ...newUser.tab_permissions?.shared, partnerships: e.target.checked },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faHandshake} />
                              <span>{t("Partnerships")}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* MANUAL MODE */}
            {createMode === "manual" && (
              <>
                {/* Account Information */}
                <div className="admin-detail-section">
                  <label>
                    <FontAwesomeIcon icon={faIdCard} />
                    {t("Account Information")}
                  </label>
                  <div className="admin-detail-grid">
                    <div className="admin-detail-field">
                      <label>{t("Username")} *</label>
                      <input
                        type="text"
                        className="profile-input"
                        value={newUser.username}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            username: e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9_]/g, ""),
                          })
                        }
                        placeholder={t("Username")}
                      />
                    </div>
                    <div className="admin-detail-field">
                      <label>{t("Email")}</label>
                      <input
                        type="email"
                        className="profile-input"
                        value={newUser.email}
                        onChange={(e) =>
                          setNewUser({ ...newUser, email: e.target.value })
                        }
                        placeholder={t(
                          "Optional - user can set on first login",
                        )}
                      />
                    </div>
                    <div className="admin-detail-field">
                      <label>{t("Phone")}</label>
                      <PhoneInput
                        value={newUser.phone_number}
                        onChange={(val) =>
                          setNewUser({ ...newUser, phone_number: val })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Location - only for sub-accounts */}
                {!newUser.is_company_admin && (
                  <div className="admin-detail-section">
                    <label>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {t("Location")}
                    </label>
                    <div className="admin-detail-grid">
                      <div className="admin-detail-field">
                        <label>{t("Location Name")}</label>
                        <input
                          type="text"
                          className="profile-input"
                          value={newUser.company_name}
                          onChange={(e) =>
                            setNewUser({
                              ...newUser,
                              company_name: e.target.value,
                            })
                          }
                          placeholder={t("Branch or site name")}
                        />
                      </div>
                      <div className="admin-detail-field">
                        <label>{t("Address")}</label>
                        <input
                          type="text"
                          className="profile-input"
                          value={newUser.address}
                          onChange={(e) =>
                            setNewUser({ ...newUser, address: e.target.value })
                          }
                          placeholder={t("Street address")}
                        />
                      </div>
                      <div className="admin-detail-field">
                        <label>{t("City")}</label>
                        <input
                          type="text"
                          className="profile-input"
                          value={newUser.city}
                          onChange={(e) =>
                            setNewUser({ ...newUser, city: e.target.value })
                          }
                          placeholder={t("City")}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Permissions - only for sub-accounts */}
                {!newUser.is_company_admin && (
                  <div className="admin-detail-section">
                    <label>
                      <FontAwesomeIcon icon={faShieldAlt} />
                      {t("Permissions")}
                    </label>
                    <div className="permissions-customize-panel">
                      {/* Donor Permissions */}
                      <div
                        className={`admin-permission-group ${newUser.can_donate ? "expanded" : ""}`}
                      >
                        <div
                          className="admin-permission-header"
                          onClick={() =>
                            setNewUser({
                              ...newUser,
                              can_donate: !newUser.can_donate,
                              tab_permissions: !newUser.can_donate
                                ? {
                                    ...newUser.tab_permissions,
                                    donor: DEFAULT_TAB_PERMISSIONS.donor,
                                  }
                                : newUser.tab_permissions,
                            })
                          }
                        >
                          <div className="admin-permission-toggle">
                            <FontAwesomeIcon
                              icon={
                                newUser.can_donate ? faToggleOn : faToggleOff
                              }
                              className={newUser.can_donate ? "active" : ""}
                            />
                          </div>
                          <FontAwesomeIcon
                            icon={faHandHoldingHeart}
                            className="admin-permission-icon donor"
                          />
                          <span className="admin-permission-title">
                            {t("Donor Access")}
                          </span>
                          <FontAwesomeIcon
                            icon={
                              newUser.can_donate ? faChevronUp : faChevronDown
                            }
                            className="admin-permission-chevron"
                          />
                        </div>
                        {!!newUser.can_donate && (
                          <div className="admin-permission-tabs">
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.donor
                                    ?.donate_materials ?? true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      donor: {
                                        ...newUser.tab_permissions?.donor,
                                        donate_materials: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faGift} />
                              <span>{t("Donate Materials")}</span>
                            </label>
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.donor?.allocate ??
                                  true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      donor: {
                                        ...newUser.tab_permissions?.donor,
                                        allocate: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faExchangeAlt} />
                              <span>{t("Allocate")}</span>
                            </label>
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.donor?.inventory ??
                                  true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      donor: {
                                        ...newUser.tab_permissions?.donor,
                                        inventory: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faWarehouse} />
                              <span>{t("Inventory")}</span>
                            </label>
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.donor?.ghg ?? true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      donor: {
                                        ...newUser.tab_permissions?.donor,
                                        ghg: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faLeaf} />
                              <span>{t("GHG Dashboard")}</span>
                            </label>
                          </div>
                        )}
                      </div>

                      {/* Receiver Permissions */}
                      <div
                        className={`admin-permission-group ${newUser.can_receive ? "expanded" : ""}`}
                      >
                        <div
                          className="admin-permission-header"
                          onClick={() =>
                            setNewUser({
                              ...newUser,
                              can_receive: !newUser.can_receive,
                              tab_permissions: !newUser.can_receive
                                ? {
                                    ...newUser.tab_permissions,
                                    receiver: DEFAULT_TAB_PERMISSIONS.receiver,
                                  }
                                : newUser.tab_permissions,
                            })
                          }
                        >
                          <div className="admin-permission-toggle">
                            <FontAwesomeIcon
                              icon={
                                newUser.can_receive ? faToggleOn : faToggleOff
                              }
                              className={newUser.can_receive ? "active" : ""}
                            />
                          </div>
                          <FontAwesomeIcon
                            icon={faBoxOpen}
                            className="admin-permission-icon receiver"
                          />
                          <span className="admin-permission-title">
                            {t("Receiver Access")}
                          </span>
                          <FontAwesomeIcon
                            icon={
                              newUser.can_receive ? faChevronUp : faChevronDown
                            }
                            className="admin-permission-chevron"
                          />
                        </div>
                        {!!newUser.can_receive && (
                          <div className="admin-permission-tabs">
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.receiver
                                    ?.browse_materials ?? true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      receiver: {
                                        ...newUser.tab_permissions?.receiver,
                                        browse_materials: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faShoppingCart} />
                              <span>{t("Browse Materials")}</span>
                            </label>
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.receiver?.schedule ??
                                  true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      receiver: {
                                        ...newUser.tab_permissions?.receiver,
                                        schedule: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faCalendarAlt} />
                              <span>{t("Schedule")}</span>
                            </label>
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.receiver
                                    ?.inventory ?? true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      receiver: {
                                        ...newUser.tab_permissions?.receiver,
                                        inventory: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faWarehouse} />
                              <span>{t("Inventory")}</span>
                            </label>
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={
                                  newUser.tab_permissions?.receiver?.ghg ?? true
                                }
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      receiver: {
                                        ...newUser.tab_permissions?.receiver,
                                        ghg: e.target.checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faLeaf} />
                              <span>{t("GHG Dashboard")}</span>
                            </label>
                          </div>
                        )}
                      </div>

                        {/* Shared Permissions */}
                        <div className="admin-permission-group expanded" style={{ marginTop: "8px" }}>
                          <div className="admin-permission-header" style={{ cursor: "default" }}>
                            <FontAwesomeIcon icon={faHandshake} className="admin-permission-icon" style={{ color: "#0d9488" }} />
                            <span className="admin-permission-title">{t("Shared Features")}</span>
                          </div>
                          <div className="admin-permission-tabs">
                            <label className="admin-tab-checkbox">
                              <input
                                type="checkbox"
                                checked={newUser.tab_permissions?.shared?.partnerships ?? true}
                                onChange={(e) =>
                                  setNewUser({
                                    ...newUser,
                                    tab_permissions: {
                                      ...newUser.tab_permissions,
                                      shared: { ...newUser.tab_permissions?.shared, partnerships: e.target.checked },
                                    },
                                  })
                                }
                              />
                              <FontAwesomeIcon icon={faHandshake} />
                              <span>{t("Partnerships")}</span>
                            </label>
                          </div>
                        </div>
                    </div>
                  </div>
                )}

                {/* Temporary Password */}
                <div className="admin-detail-section">
                  <label>
                    <FontAwesomeIcon icon={faLock} />
                    {t("Security")}
                  </label>
                  <div className="admin-password-row">
                    <div className="password-input-wrapper">
                      <input
                        type={showTempPassword ? "text" : "password"}
                        className="profile-input"
                        value={newUser.temp_password}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            temp_password: e.target.value,
                          })
                        }
                        placeholder={t("Temporary password")}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowTempPassword(!showTempPassword)}
                        title={
                          showTempPassword
                            ? t("Hide password")
                            : t("Show password")
                        }
                      >
                        <FontAwesomeIcon
                          icon={showTempPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="admin-generate-btn"
                      onClick={generateTempPassword}
                    >
                      {t("Generate")}
                    </button>
                  </div>
                </div>

                {/* Footer with create button */}
                <div className="admin-detail-footer">
                  <ActionButton
                    onClick={() => {
                      resetNewUserForm();
                      setShowCreateModal(false);
                    }}
                    variant="default"
                  >
                    {t("Cancel")}
                  </ActionButton>
                  <ActionButton
                    onClick={handleManualCreate}
                    variant="secondary"
                    disabled={
                      !newUser.organization_id ||
                      !newUser.username.trim() ||
                      !newUser.temp_password.trim()
                    }
                    loading={isCreating}
                  >
                    {t("Create User")}
                  </ActionButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* inline edit panel */}
      {editingUser && !showCreateModal && (
        <div
          className="admin-detail-panel"
          ref={detailPanelRef}
          style={{ scrollMarginTop: "100px" }}
        >
          <div className="admin-detail-header-v2">
            <div className="admin-header-left">
              <div className={`user-avatar ${getUserTypeClass(editingUser)}`}>
                <FontAwesomeIcon icon={getUserTypeIcon(editingUser)} />
              </div>
              <div className="admin-header-info">
                <h3>{editingUser.username}</h3>
                <div className="admin-header-meta">
                  <span
                    className={`type-badge small ${getUserTypeClass(editingUser)}`}
                  >
                    {getUserTypeLabel(editingUser)}
                  </span>
                </div>
              </div>
            </div>
            <div className="admin-header-right">
              {!editingUser.is_master_admin && (
                <div className="admin-header-status">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={editingUser.is_active}
                      onChange={() =>
                        setEditingUser({
                          ...editingUser,
                          is_active: !editingUser.is_active,
                        })
                      }
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span
                    className={`toggle-label ${editingUser.is_active ? "active" : "inactive"}`}
                  >
                    {editingUser.is_active ? t("Active") : t("Inactive")}
                  </span>
                </div>
              )}
              <button
                className="admin-detail-close"
                onClick={() => tryCloseEdit()}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          {/* User ID - copyable */}
          <div className="admin-user-id-row">
            <span className="admin-user-id-label">{t("User ID")}:</span>
            <code className="admin-user-id-value">{editingUser.id}</code>
            <button
              className="admin-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(editingUser.id);
                // Could add toast feedback here
              }}
              title={t("Copy to clipboard")}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>

          <div className="admin-detail-body">
            {/* Account Information Section */}
            <div className="admin-detail-section">
              <label>
                <FontAwesomeIcon icon={faIdCard} />
                {t("Account Information")}
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Username")}</label>
                  <input
                    type="text"
                    className="profile-input"
                    value={editingUser.username}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        username: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Email")}</label>
                  <input
                    type="email"
                    className="profile-input"
                    value={editingUser.email}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, email: e.target.value })
                    }
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Phone")}</label>
                  <PhoneInput
                    value={editingUser.phone_number || ""}
                    onChange={(val) =>
                      setEditingUser({ ...editingUser, phone_number: val })
                    }
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Organization")}</label>
                  <select
                    className="profile-input"
                    value={editingUser.organization_id || ""}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        organization_id: e.target.value || null,
                        organization_name:
                          organizations.find((o) => o.id === e.target.value)
                            ?.name || null,
                      })
                    }
                    disabled={editingUser.is_master_admin}
                  >
                    <option value="">{t("No organization")}</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {!!editingUser.is_company_admin &&
                    originalEditingUser?.organization_id !==
                      editingUser.organization_id &&
                    editingUser.organization_id && (
                      <div className="admin-field-warning">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>
                          {t(
                            "Warning: This user will become admin of the new organization",
                          )}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Location Section - show for sub-accounts OR promoted company admins with location */}
            {!editingUser.is_master_admin &&
              (!editingUser.is_company_admin || editingUser.address) && (
                <div className="admin-detail-section">
                  <label>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {t("Location")}
                  </label>
                  <div className="admin-detail-grid">
                    <div className="admin-detail-field">
                      <label>{t("Address")}</label>
                      <input
                        type="text"
                        className="profile-input"
                        value={editingUser.address || ""}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            address: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="admin-detail-field">
                      <label>{t("City")}</label>
                      <input
                        type="text"
                        className="profile-input"
                        value={editingUser.city || ""}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            city: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* Role & Permissions Section - only for non-master admins */}
            {!editingUser.is_master_admin && (
              <div className="admin-detail-section">
                <label>
                  <FontAwesomeIcon icon={faShieldAlt} />
                  {t("Role & Permissions")}
                </label>

                {/* Account Type Toggle */}
                <div className="admin-subsection">
                  <label className="admin-subsection-label">
                    {t("Account Type")}
                  </label>
                  <div className="admin-type-toggle">
                    <button
                      className={`admin-type-btn ${!editingUser.is_company_admin ? "active" : ""}`}
                      onClick={() =>
                        setEditingUser({
                          ...editingUser,
                          is_company_admin: false,
                        })
                      }
                      disabled={!canDemote && editingUser.is_company_admin}
                    >
                      <FontAwesomeIcon icon={faUser} />
                      {t("Company User")}
                    </button>
                    <button
                      className={`admin-type-btn ${editingUser.is_company_admin ? "active" : ""}`}
                      onClick={() =>
                        setEditingUser({
                          ...editingUser,
                          is_company_admin: true,
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faUserTie} />
                      {t("Company Admin")}
                    </button>
                  </div>
                  {!canDemote && editingUser.is_company_admin && (
                    <small className="admin-input-hint warning">
                      <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                      {t("Only Site Admins can demote Company Admins")}
                    </small>
                  )}
                </div>

                {/* Permissions */}
                <div className="admin-subsection">
                  <label className="admin-subsection-label">
                    {t("Permissions")}
                    <Tooltip
                      title={t(
                        "Control which features and tabs this user can access",
                      )}
                      placement="top"
                      arrow
                    >
                      <span className="admin-info-icon">
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    </Tooltip>
                  </label>

                  {/* Donor Permissions */}
                  <div
                    className={`admin-permission-group ${editingUser.can_donate ? "expanded" : ""}`}
                  >
                    <div
                      className="admin-permission-header"
                      onClick={() =>
                        setEditingUser({
                          ...editingUser,
                          can_donate: !editingUser.can_donate,
                          // Reset tab permissions when disabling
                          tab_permissions: !editingUser.can_donate
                            ? {
                                ...editingUser.tab_permissions,
                                donor: DEFAULT_TAB_PERMISSIONS.donor,
                              }
                            : editingUser.tab_permissions,
                        })
                      }
                    >
                      <div className="admin-permission-toggle">
                        <FontAwesomeIcon
                          icon={
                            editingUser.can_donate ? faToggleOn : faToggleOff
                          }
                          className={editingUser.can_donate ? "active" : ""}
                        />
                      </div>
                      <FontAwesomeIcon
                        icon={faHandHoldingHeart}
                        className="admin-permission-icon donor"
                      />
                      <span className="admin-permission-title">
                        {t("Donor Access")}
                      </span>
                      <FontAwesomeIcon
                        icon={
                          editingUser.can_donate ? faChevronUp : faChevronDown
                        }
                        className="admin-permission-chevron"
                      />
                    </div>

                    {!!editingUser.can_donate && (
                      <div className="admin-permission-tabs">
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.donor
                                ?.donate_materials ?? true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  donor: {
                                    ...editingUser.tab_permissions?.donor,
                                    donate_materials: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faGift} />
                          <span>{t("Donate Materials")}</span>
                        </label>
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.donor?.allocate ??
                              true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  donor: {
                                    ...editingUser.tab_permissions?.donor,
                                    allocate: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faExchangeAlt} />
                          <span>{t("Allocate")}</span>
                        </label>
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.donor?.inventory ??
                              true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  donor: {
                                    ...editingUser.tab_permissions?.donor,
                                    inventory: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faWarehouse} />
                          <span>{t("Inventory")}</span>
                        </label>
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.donor?.ghg ?? true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  donor: {
                                    ...editingUser.tab_permissions?.donor,
                                    ghg: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faLeaf} />
                          <span>{t("GHG Dashboard")}</span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Receiver Permissions */}
                  <div
                    className={`admin-permission-group ${editingUser.can_receive ? "expanded" : ""}`}
                  >
                    <div
                      className="admin-permission-header"
                      onClick={() =>
                        setEditingUser({
                          ...editingUser,
                          can_receive: !editingUser.can_receive,
                          tab_permissions: !editingUser.can_receive
                            ? {
                                ...editingUser.tab_permissions,
                                receiver: DEFAULT_TAB_PERMISSIONS.receiver,
                              }
                            : editingUser.tab_permissions,
                        })
                      }
                    >
                      <div className="admin-permission-toggle">
                        <FontAwesomeIcon
                          icon={
                            editingUser.can_receive ? faToggleOn : faToggleOff
                          }
                          className={editingUser.can_receive ? "active" : ""}
                        />
                      </div>
                      <FontAwesomeIcon
                        icon={faBoxOpen}
                        className="admin-permission-icon receiver"
                      />
                      <span className="admin-permission-title">
                        {t("Receiver Access")}
                      </span>
                      <FontAwesomeIcon
                        icon={
                          editingUser.can_receive ? faChevronUp : faChevronDown
                        }
                        className="admin-permission-chevron"
                      />
                    </div>

                    {!!editingUser.can_receive && (
                      <div className="admin-permission-tabs">
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.receiver
                                ?.browse_materials ?? true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  receiver: {
                                    ...editingUser.tab_permissions?.receiver,
                                    browse_materials: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faShoppingCart} />
                          <span>{t("Browse Materials")}</span>
                        </label>
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.receiver?.schedule ??
                              true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  receiver: {
                                    ...editingUser.tab_permissions?.receiver,
                                    schedule: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{t("Schedule")}</span>
                        </label>
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.receiver
                                ?.inventory ?? true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  receiver: {
                                    ...editingUser.tab_permissions?.receiver,
                                    inventory: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faWarehouse} />
                          <span>{t("Inventory")}</span>
                        </label>
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              editingUser.tab_permissions?.receiver?.ghg ?? true
                            }
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  receiver: {
                                    ...editingUser.tab_permissions?.receiver,
                                    ghg: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faLeaf} />
                          <span>{t("GHG Dashboard")}</span>
                        </label>
                      </div>
                    )}
                  </div>

                    {/* Shared Permissions */}
                    <div className="admin-permission-group expanded" style={{ marginTop: "8px" }}>
                      <div className="admin-permission-header" style={{ cursor: "default" }}>
                        <FontAwesomeIcon icon={faHandshake} className="admin-permission-icon" style={{ color: "#0d9488" }} />
                        <span className="admin-permission-title">{t("Shared Features")}</span>
                      </div>
                      <div className="admin-permission-tabs">
                        <label className="admin-tab-checkbox">
                          <input
                            type="checkbox"
                            checked={editingUser.tab_permissions?.shared?.partnerships ?? true}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                tab_permissions: {
                                  ...editingUser.tab_permissions,
                                  shared: { ...editingUser.tab_permissions?.shared, partnerships: e.target.checked },
                                },
                              })
                            }
                          />
                          <FontAwesomeIcon icon={faHandshake} />
                          <span>{t("Partnerships")}</span>
                        </label>
                      </div>
                    </div>
                </div>
              </div>
            )}

            {/* Security Section - not for master admins */}
            {!editingUser.is_master_admin &&
              (() => {
                const passwordValidation = validatePassword(resetPassword);
                const canSetPassword = passwordValidation.valid;

                return (
                  <div className="admin-detail-section">
                    <label>
                      <FontAwesomeIcon icon={faLock} />
                      {t("Security")}
                    </label>
                    <div className="admin-password-reset">
                      <div className="admin-password-row">
                        <div className="password-input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="profile-input"
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            placeholder={t("New temporary password")}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            title={
                              showPassword
                                ? t("Hide password")
                                : t("Show password")
                            }
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                            />
                          </button>
                        </div>
                        <button
                          type="button"
                          className="admin-generate-btn"
                          onClick={generateResetPassword}
                        >
                          {t("Generate")}
                        </button>
                      </div>
                      {resetPassword && !canSetPassword && (
                        <small className="admin-input-hint warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                          {t(passwordValidation.error)}
                        </small>
                      )}
                      <div className="admin-password-actions">
                        <ActionButton
                          onClick={handleSetPassword}
                          variant={canSetPassword ? "warning" : "default"}
                          disabled={!canSetPassword || isSaving}
                          loading={isSaving}
                          size="small"
                        >
                          {t("Set Password")}
                        </ActionButton>
                        <small className="admin-input-hint">
                          {t("Min 8 chars, uppercase, lowercase, number")}
                        </small>
                      </div>
                    </div>
                  </div>
                );
              })()}
          </div>

          {(hasFormChanges(originalEditingUser, editingUser) ||
            resetPassword) && (
            <div className="admin-detail-footer">
              <ActionButton onClick={() => tryCloseEdit()} variant="default">
                {t("Cancel")}
              </ActionButton>
              <ActionButton
                onClick={handleSaveEdit}
                variant="secondary"
                loading={isSaving}
              >
                {t("Save Changes")}
              </ActionButton>
            </div>
          )}
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && (
        <div
          className="credentials-modal-overlay"
          onClick={() => setShowCredentialsModal(false)}
        >
          <div
            className="credentials-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="credentials-modal-header">
              <FontAwesomeIcon
                icon={faCheck}
                className="credentials-success-icon"
              />
              <h3>{t("Credentials Generated")}</h3>
              <p>{t("Copy these credentials before closing")}</p>
            </div>
            {credentialsToShow.expiresAt && (
              <div className="credentials-expiry-warning">
                <FontAwesomeIcon icon={faClock} />
                <span>
                  {t("Expires in 48 hours")} —{" "}
                  {t("User must log in and set password before then")}
                </span>
              </div>
            )}
            <div className="credentials-modal-body">
              <div
                className={`credentials-copy-box ${copiedField === "username" ? "copied" : ""}`}
                onClick={() => {
                  navigator.clipboard.writeText(credentialsToShow.username);
                  setCopiedField("username");
                  setTimeout(() => setCopiedField(null), 2000);
                }}
              >
                <div className="credentials-copy-label">{t("Username")}</div>
                <div className="credentials-copy-value">
                  {credentialsToShow.username}
                </div>
                <FontAwesomeIcon
                  icon={copiedField === "username" ? faCheck : faCopy}
                  className="credentials-copy-icon"
                />
              </div>
              <div
                className={`credentials-copy-box ${copiedField === "password" ? "copied" : ""}`}
                onClick={() => {
                  navigator.clipboard.writeText(credentialsToShow.password);
                  setCopiedField("password");
                  setTimeout(() => setCopiedField(null), 2000);
                }}
              >
                <div className="credentials-copy-label">
                  {t("Temporary Password")}
                </div>
                <div className="credentials-copy-value">
                  {credentialsToShow.password}
                </div>
                <FontAwesomeIcon
                  icon={copiedField === "password" ? faCheck : faCopy}
                  className="credentials-copy-icon"
                />
              </div>
              <div
                className={`credentials-copy-box copy-all ${copiedField === "all" ? "copied" : ""}`}
                onClick={() => {
                  const copyText = credentialsToShow.expiresAt
                    ? `${t("Username")}: ${credentialsToShow.username}\n${t("Temporary Password")}: ${credentialsToShow.password}\n${t("Note")}: ${t("Credentials expire in 48 hours. Please log in to set your permanent password.")}`
                    : `${t("Username")}: ${credentialsToShow.username}\n${t("Password")}: ${credentialsToShow.password}`;
                  navigator.clipboard.writeText(copyText);
                  setCopiedField("all");
                  setTimeout(() => setCopiedField(null), 2000);
                }}
              >
                <div className="credentials-copy-label">{t("Copy All")}</div>
                <div className="credentials-copy-value copy-all-preview">
                  {credentialsToShow.username} / {credentialsToShow.password}
                </div>
                <FontAwesomeIcon
                  icon={copiedField === "all" ? faCheck : faCopy}
                  className="credentials-copy-icon"
                />
              </div>
            </div>
            {credentialsToShow.expiresAt && (
              <div className="credentials-modal-note">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  {t(
                    "The user will set their email and password on first login.",
                  )}
                </span>
              </div>
            )}
            <div className="credentials-modal-footer">
              <ActionButton
                onClick={() => setShowCredentialsModal(false)}
                variant="secondary"
              >
                {t("Done")}
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="credentials-modal-overlay" onClick={cancelDiscard}>
          <div className="unsaved-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-modal-header">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="unsaved-warning-icon"
              />
              <h3>{t("Unsaved Changes")}</h3>
            </div>
            <div className="unsaved-modal-body">
              <p>
                {t(
                  "You have unsaved changes. Are you sure you want to discard them?",
                )}
              </p>
            </div>
            <div className="unsaved-modal-footer">
              <ActionButton onClick={cancelDiscard} variant="default">
                {t("Keep Editing")}
              </ActionButton>
              <ActionButton onClick={confirmDiscard} variant="danger">
                {t("Discard Changes")}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagementSection;
